import { AUTO_SCROLL_EDGE, AUTO_SCROLL_MAX_SPEED, GRID_GAP } from '../dashboard-constants';

// Auto-scroll velocity in px/s (negative = up). Zero outside the edge bands; eases in quadratically
// inside one, and holds at the max past the edge.
export function autoScrollSpeed(pointerY: number, top: number, bottom: number): number {
  // A scroller shorter than both bands would be in both at once; split it down the middle instead.
  const edge = Math.min(AUTO_SCROLL_EDGE, (bottom - top) / 2);
  if (edge <= 0) return 0;

  const intoTop = top + edge - pointerY;
  if (intoTop > 0) return -AUTO_SCROLL_MAX_SPEED * Math.min(1, intoTop / edge) ** 2;

  const intoBottom = pointerY - (bottom - edge);
  if (intoBottom > 0) return AUTO_SCROLL_MAX_SPEED * Math.min(1, intoBottom / edge) ** 2;

  return 0;
}

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface HitCard {
  id: string;
  rect: Box;
}

// Where a drop would put the dragged card: `index` into the zone's cards with the dragged card
// removed, and `marker`, the line to draw there (zero-width for a vertical line, zero-height for a
// horizontal one), in the same coordinates as the boxes passed in.
export interface Insertion {
  index: number;
  marker: Box;
}

// Cards at least this share of the grid's width split before/after by top/bottom half. A ratio
// rather than the `wide` preset so the narrow single-column layout behaves the same.
const FULL_WIDTH_RATIO = 0.9;

const HALF_GAP = GRID_GAP / 2;

function distanceToBox(px: number, py: number, box: Box): number {
  const dx = Math.max(box.left - px, 0, px - box.right);
  const dy = Math.max(box.top - py, 0, py - box.bottom);
  return Math.hypot(dx, dy);
}

const horizontalLine = (y: number, left: number, right: number): Box => ({
  left,
  top: y,
  right,
  bottom: y
});

const verticalLine = (x: number, top: number, bottom: number): Box => ({
  left: x,
  top,
  right: x,
  bottom
});

/**
 * Resolve the pointer to an index in the zone's ARRAY order, which is all a drop changes; autoCompact
 * derives positions from that order. The marker is exact about order but not always about
 * placement: autoCompact may stack the card under a one-third rather than beside it, and below
 * 1200px the grid shows reading order, which differs from array order around stacked pairs.
 *
 * `cards` is in array order and includes the dragged card if it lives in this zone. Returns null
 * when the drop would change nothing. Below every card appends, above every card inserts first;
 * otherwise the nearest card (preferring the pointer's row) is the target, and the pointer's side of
 * its centre picks before/after.
 */
export function resolveInsertion(
  px: number,
  py: number,
  cards: HitCard[],
  draggedId: string,
  bounds: Box
): Insertion | null {
  const peers = cards.filter(c => c.id !== draggedId);
  const ownIndex = cards.findIndex(c => c.id === draggedId);
  const unlessNoOp = (insertion: Insertion) => (insertion.index === ownIndex ? null : insertion);

  if (peers.length === 0) {
    return unlessNoOp({ index: 0, marker: horizontalLine(bounds.top, bounds.left, bounds.right) });
  }

  const top = Math.min(...cards.map(c => c.rect.top));
  const bottom = Math.max(...cards.map(c => c.rect.bottom));
  if (py > bottom) {
    return unlessNoOp({
      index: peers.length,
      marker: horizontalLine(bottom + HALF_GAP, bounds.left, bounds.right)
    });
  }
  if (py < top) {
    return unlessNoOp({
      index: 0,
      marker: horizontalLine(top - HALF_GAP, bounds.left, bounds.right)
    });
  }

  // Prefer the cards level with the pointer: empty space to the right of a short row's last card
  // means "after that card", even when a card in the row above is closer as the crow flies.
  const level = cards.filter(c => py >= c.rect.top - HALF_GAP && py <= c.rect.bottom + HALF_GAP);
  const nearest = (level.length > 0 ? level : cards).reduce((best, c) =>
    distanceToBox(px, py, c.rect) < distanceToBox(px, py, best.rect) ? c : best
  );
  if (nearest.id === draggedId) return null;

  const { rect } = nearest;
  const peerIndex = peers.indexOf(nearest);
  const isFullWidth = rect.right - rect.left >= (bounds.right - bounds.left) * FULL_WIDTH_RATIO;

  if (isFullWidth) {
    const isBefore = py < (rect.top + rect.bottom) / 2;
    return unlessNoOp({
      index: isBefore ? peerIndex : peerIndex + 1,
      marker: horizontalLine(
        isBefore ? rect.top - HALF_GAP : rect.bottom + HALF_GAP,
        rect.left,
        rect.right
      )
    });
  }

  const isBefore = px < (rect.left + rect.right) / 2;
  return unlessNoOp({
    index: isBefore ? peerIndex : peerIndex + 1,
    marker: verticalLine(
      isBefore ? rect.left - HALF_GAP : rect.right + HALF_GAP,
      rect.top,
      rect.bottom
    )
  });
}
